import Box from '@mui/material/Box';
import { type JSX, useCallback, useMemo, useState } from 'react';
import { generatePath } from 'react-router';

import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { Accordion } from 'Components/Accordion/Accordion';
import { AccordionCollapse } from 'Components/Accordion/AccordionCollapse/AccordionCollapse';
import { Button } from 'Components/Buttons/Buttons';
import { OverflowMenu, OverflowMenuProps } from 'Components/Buttons/OverflowMenu';
import { RBACComponent } from 'Components/Context/RBACComponent';
import { Role } from 'Components/Context/RBACContext';
import { FixedFooter } from 'Components/FixedFooter/FixedFooter';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { AddOrUpdateIrqQuestionModal, AddOrUpdateIrqQuestionModalProps } from 'Components/Modal/AddOrUpdateIrqQuestionModal/AddOrUpdateIrqQuestionModal';
import { AddOrUpdateTextModal, AddOrUpdateTextModalProps } from 'Components/Modal/AddOrUpdateTextModal/AddOrUpdateTextModal';
import { Modal } from 'Components/Modal/Modal';
import { ReorderModal, ReorderModalProps } from 'Components/Modal/ReorderModal/ReorderModal';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageDataLoader } from 'Components/PageDataLoader/PageDataLoader';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Text } from 'Components/Text/Text';
import { ICON_ADD_CREATE, ICON_CLOSE, ICON_DELETE_REMOVE, ICON_DOWNLOAD, ICON_EDIT_MODIFY_UPDATE, ICON_ORDERING, ICON_SAVE } from 'Config/Icons';
import { SETTINGS, TPRM_IRQ_SETTINGS } from 'Config/PathTemplates';
import { useUnsavedChangesTracking } from 'Hooks/Forms';
import { InherentRiskQuestionnaireConfiguration as InherentRiskQuestionnaireConfigurationModel, InherentRiskQuestionnaireSectionConfiguration, IrqAnswerOption, IrqQuestion, IrqQuestionMultipleSelect, IrqQuestionSingleSelect, IrqQuestionType, RiskRatingPointThresholds } from 'Models/InherentRisk';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';

import styles from './InherentRiskQuestionnaireConfiguration.module.css';
import { InherentRiskQuestionnaireConfigurationRow } from './InherentRiskQuestionnaireConfigurationRow/InherentRiskQuestionnaireConfigurationRow';
import { InherentRiskQuestionnaireConfigurationThresholds, InherentRiskQuestionnaireConfigurationThresholdsProps } from './InherentRiskQuestionnaireConfigurationThresholds/InherentRiskQuestionnaireConfigurationThresholds';

type ActiveModal =
    | {
          type: 'addSection';
      }
    | {
          type: 'addQuestion';
          section: InherentRiskQuestionnaireSectionConfiguration;
      }
    | {
          type: 'addOption';
          section: InherentRiskQuestionnaireSectionConfiguration;
          question: IrqQuestionSingleSelect | IrqQuestionMultipleSelect;
      }
    | {
          type: 'reorderSections';
      }
    | {
          type: 'reorderQuestions';
          section: InherentRiskQuestionnaireSectionConfiguration;
      }
    | {
          type: 'reorderOptions';
          section: InherentRiskQuestionnaireSectionConfiguration;
          question: IrqQuestionSingleSelect | IrqQuestionMultipleSelect;
      }
    | {
          type: 'renameSection';
          section: InherentRiskQuestionnaireSectionConfiguration;
      }
    | {
          type: 'editQuestion';
          section: InherentRiskQuestionnaireSectionConfiguration;
          questionIndex: number;
      }
    | {
          type: 'renameOption';
          section: InherentRiskQuestionnaireSectionConfiguration;
          question: IrqQuestionSingleSelect | IrqQuestionMultipleSelect;
          answer: IrqAnswerOption;
      }
    | {
          type: 'deleteSection';
          section: InherentRiskQuestionnaireSectionConfiguration;
      }
    | {
          type: 'deleteQuestion';
          section: InherentRiskQuestionnaireSectionConfiguration;
          question: IrqQuestion;
      }
    | {
          type: 'deleteOption';
          section: InherentRiskQuestionnaireSectionConfiguration;
          question: IrqQuestionSingleSelect | IrqQuestionMultipleSelect;
          answer: IrqAnswerOption;
      };

export interface InherentRiskQuestionnaireConfigurationLoaderProps {
    module: DueDiligenceModule;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
}

export const InherentRiskQuestionnaireConfigurationLoader = (props: InherentRiskQuestionnaireConfigurationLoaderProps): JSX.Element => {
    return <PageDataLoader path={TPRM_IRQ_SETTINGS} fetchData={() => props.moduleApi.getInherentRiskQuestionnaireConfiguration()} renderForm={(result) => <InherentRiskQuestionnaireConfiguration initialConfiguration={result.data} module={props.module} moduleApi={props.moduleApi} />} />;
};

interface InherentRiskQuestionnaireConfigurationProps {
    initialConfiguration: InherentRiskQuestionnaireConfigurationModel;
    module: DueDiligenceModule;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
}

// TODO [TPRM - IRQ]: It's possible to save the IRQ configuration with "invalid" thresholds. Consider adding additional validation on the frontend and backend. The only validation we do is that each successive threshold is greater than the last. But the user can successfully save IRQ configuration while there are red Xs showing in the thresholds table. At least one way to make this scenario happen is to delete a couple out-of-the-box questions, then click "Save". (The red Xs show because the maximum number in the table no longer matches the actual maximum possible score, but this does not factor into the validation that would prevent the user from saving.)
const InherentRiskQuestionnaireConfiguration = (props: InherentRiskQuestionnaireConfigurationProps) => {
    const [activeModal, setActiveModal] = useState<ActiveModal>();
    const [requestState, setRequestState] = useState<{ type: 'inProgress' } | { type: 'failure'; message: string }>();

    const [thresholds, setThresholds] = useState<RiskRatingPointThresholds>(props.initialConfiguration.thresholds);
    const [sections, setSections] = useState<InherentRiskQuestionnaireSectionConfiguration[]>(props.initialConfiguration.sections);

    const configuration = useMemo((): InherentRiskQuestionnaireConfigurationModel => ({ thresholds, sections }), [thresholds, sections]);
    const [hasUnsavedChanges, onConfigurationSaved] = useUnsavedChangesTracking(configuration, false);

    const [minimumPossibleScore, maximumPossibleScore] = useMemo(() => {
        let minimum = 0;
        let maximum = 0;

        sections.forEach((section) => {
            section.questions.forEach((question) => {
                if (question._type === IrqQuestionType.SINGLE_SELECT) {
                    const points = question.options.map((o) => o.points);
                    const questionMinimum = Math.min(...points);
                    const questionMaximum = Math.max(...points);

                    minimum += questionMinimum;
                    maximum += questionMaximum;
                } else if (question._type === IrqQuestionType.MULTIPLE_SELECT) {
                    const points = question.options.map((o) => o.points);
                    const questionMinimum = Math.min(0, ...points); // Minimum is either 0 (no selections) or the lowest point value (if negative).
                    const questionMaximum = points.reduce((sum, val) => sum + (val > 0 ? val : 0), 0); // Maximum is the sum of all positive point values.

                    minimum += questionMinimum;
                    maximum += questionMaximum;
                }
            });
        });

        return [minimum, maximum];
    }, [sections]);

    // This MUST be a useCallback. The InherentRiskQuestionnaireConfigurationThresholds component includes this function as a useEffect dependency; if this function isn't memoized, an infinite loop will occur.
    const handleThresholdsChange = useCallback((newThresholds: number[]) => {
        setThresholds({
            low: newThresholds[0],
            low_moderate: newThresholds[1],
            moderate: newThresholds[2],
            moderate_high: newThresholds[3],
        });
    }, []);

    const hideModal = () => setActiveModal(undefined);

    const addSectionProps: AddOrUpdateTextModalProps = {
        modalTitle: 'Add Section',
        fieldName: 'Section name',
        onConfirm: (sectionName: string) => {
            if (sections.some((s) => s.name === sectionName)) {
                return 'There is already a section with this name.';
            } else {
                setSections([...sections, { name: sectionName, questions: [] }]);
            }
        },
        onClose: hideModal,
    };

    const getAddQuestionProps = (section: InherentRiskQuestionnaireSectionConfiguration): AddOrUpdateIrqQuestionModalProps => {
        return {
            onSaved: (newQuestion: IrqQuestion) => setSections([...sections.map((s) => (s.name === section.name ? { ...s, questions: [...s.questions, newQuestion] } : s))]),
            onHideModal: hideModal,
        };
    };

    const getAddOptionProps = (section: InherentRiskQuestionnaireSectionConfiguration, question: IrqQuestionSingleSelect | IrqQuestionMultipleSelect): AddOrUpdateTextModalProps => {
        return {
            modalTitle: 'Add Option',
            fieldName: 'Option text',
            onConfirm: (answerText: string) => setSections([...sections.map((s) => (s.name === section.name ? { ...s, questions: s.questions.map((q) => (q.text === question.text && (q._type === IrqQuestionType.SINGLE_SELECT || q._type === IrqQuestionType.MULTIPLE_SELECT) ? { ...q, options: [...q.options, { text: answerText, points: 0 }] } : q)) } : s))]),
            onClose: hideModal,
        };
    };

    const reorderSectionsProps: ReorderModalProps<InherentRiskQuestionnaireSectionConfiguration> = {
        title: 'Reorder Sections',
        items: sections,
        getItemId: (section) => section.name,
        getItemDescription: (section) => section.name,
        onOrderSelected: (sections) => setSections([...sections]),
        onClose: hideModal,
    };

    const getReorderQuestionsProps = (section: InherentRiskQuestionnaireSectionConfiguration): ReorderModalProps<IrqQuestion> => {
        return {
            title: 'Reorder Questions',
            items: section.questions,
            getItemId: (question) => question.text,
            getItemDescription: (question) => question.text,
            onOrderSelected: (questions) => setSections([...sections.map((s) => (s.name === section.name ? { ...s, questions } : s))]),
            onClose: hideModal,
        };
    };

    const getReorderOptionsProps = (section: InherentRiskQuestionnaireSectionConfiguration, question: IrqQuestionSingleSelect | IrqQuestionMultipleSelect): ReorderModalProps<IrqAnswerOption> => {
        return {
            title: 'Reorder Options',
            items: question.options,
            getItemId: (answer) => answer.text,
            getItemDescription: (answer) => answer.text,
            onOrderSelected: (items) => setSections([...sections.map((s) => (s.name === section.name ? { ...s, questions: s.questions.map((q) => (q.text === question.text ? { ...q, options: items } : q)) } : s))]),
            onClose: hideModal,
        };
    };

    const getRenameSectionModalProps = (section: InherentRiskQuestionnaireSectionConfiguration): AddOrUpdateTextModalProps => {
        return {
            modalTitle: 'Rename Section',
            fieldName: 'Section name',
            initialValue: section.name,
            onConfirm: (newName: string) => {
                if (sections.some((s) => s.name === newName)) {
                    return 'There is already a section with this name.';
                } else {
                    setSections(sections.map((s) => (s.name === section.name ? { ...s, name: newName } : s)));
                }
            },
            onClose: hideModal,
        };
    };

    const getEditQuestionModalProps = (section: InherentRiskQuestionnaireSectionConfiguration, questionIndex: number): AddOrUpdateIrqQuestionModalProps => {
        return {
            question: section.questions[questionIndex],
            onSaved: (updatedQuestion: IrqQuestion) => setSections([...sections.map((s) => (s.name === section.name ? { ...s, questions: s.questions.map((q, index) => (index === questionIndex ? updatedQuestion : q)) } : s))]),
            onHideModal: hideModal,
        };
    };

    const getRenameOptionModalProps = (section: InherentRiskQuestionnaireSectionConfiguration, question: IrqQuestionSingleSelect | IrqQuestionMultipleSelect, answer: IrqAnswerOption): AddOrUpdateTextModalProps => {
        return {
            modalTitle: 'Rename Option',
            fieldName: 'Option text',
            initialValue: answer.text,
            onConfirm: (newText: string) => setSections([...sections.map((s) => (s.name === section.name ? { ...s, questions: s.questions.map((q) => (q.text === question.text && (q._type === IrqQuestionType.SINGLE_SELECT || q._type === IrqQuestionType.MULTIPLE_SELECT) ? { ...q, options: q.options.map((o) => (o.text === answer.text ? { ...o, text: newText } : o)) } : q)) } : s))]),
            onClose: hideModal,
        };
    };

    const getDeleteSectionModalProps = (section: InherentRiskQuestionnaireSectionConfiguration): ConfirmDeleteModalProps => {
        return {
            headerText: 'Delete Section',
            confirmationText: `Are you sure you want to delete the section "${section.name}"?`,
            onConfirm: () => setSections(sections.filter((s) => s.name !== section.name)),
            hideModal: hideModal,
        };
    };

    const getDeleteQuestionModalProps = (section: InherentRiskQuestionnaireSectionConfiguration, question: IrqQuestion): ConfirmDeleteModalProps => {
        return {
            headerText: 'Delete Question',
            confirmationText: `Are you sure you want to delete the question "${question.text}"?`,
            onConfirm: () => setSections([...sections.map((s) => (s.name === section.name ? { ...s, questions: s.questions.filter((q) => q.text !== question.text) } : s))]),
            hideModal: hideModal,
        };
    };

    const getDeleteOptionModalProps = (section: InherentRiskQuestionnaireSectionConfiguration, question: IrqQuestionSingleSelect | IrqQuestionMultipleSelect, answer: IrqAnswerOption): ConfirmDeleteModalProps => {
        return {
            headerText: 'Delete Option',
            confirmationText: `Are you sure you want to delete the answer "${answer.text}"?`,
            onConfirm: () => setSections([...sections.map((s) => (s.name === section.name ? { ...s, questions: s.questions.map((q) => (q.text === question.text && (q._type === IrqQuestionType.SINGLE_SELECT || q._type === IrqQuestionType.MULTIPLE_SELECT) ? { ...q, options: q.options.filter((o) => o !== answer) } : q)) } : s))]),
            hideModal: hideModal,
        };
    };

    const getOverflowMenuPropsForSection = (section: InherentRiskQuestionnaireSectionConfiguration): OverflowMenuProps => {
        return {
            accessibilityTitle: `Menu for section "${section.name}"`,
            overflowItems: [
                { text: 'Add Question', icon: ICON_ADD_CREATE, onClickAction: () => setActiveModal({ type: 'addQuestion', section }) },
                { text: 'Reorder Questions', icon: ICON_ORDERING, onClickAction: () => setActiveModal({ type: 'reorderQuestions', section }) },
                { text: 'Rename', icon: ICON_EDIT_MODIFY_UPDATE, onClickAction: () => setActiveModal({ type: 'renameSection', section }) },
                { text: 'Delete', icon: ICON_DELETE_REMOVE, onClickAction: () => setActiveModal({ type: 'deleteSection', section }) },
            ],
        };
    };

    const getOverflowMenuPropsForQuestion = (section: InherentRiskQuestionnaireSectionConfiguration, questionIndex: number): OverflowMenuProps => {
        const question = section.questions[questionIndex];
        return {
            accessibilityTitle: `Menu for question "${question.text}" in section "${section.name}"`,
            overflowItems: [
                ...(question._type === IrqQuestionType.SINGLE_SELECT || question._type === IrqQuestionType.MULTIPLE_SELECT
                    ? [
                          { text: 'Add Option', icon: ICON_ADD_CREATE, onClickAction: () => setActiveModal({ type: 'addOption', section, question }) },
                          { text: 'Reorder Options', icon: ICON_ORDERING, onClickAction: () => setActiveModal({ type: 'reorderOptions', section, question }) },
                      ]
                    : []),
                { text: 'Edit', icon: ICON_EDIT_MODIFY_UPDATE, onClickAction: () => setActiveModal({ type: 'editQuestion', section, questionIndex }) },
                { text: 'Delete', icon: ICON_DELETE_REMOVE, onClickAction: () => setActiveModal({ type: 'deleteQuestion', section, question }) },
            ],
        };
    };

    const getOverflowMenuPropsForOption = (section: InherentRiskQuestionnaireSectionConfiguration, question: IrqQuestionSingleSelect | IrqQuestionMultipleSelect, answer: IrqAnswerOption): OverflowMenuProps => {
        return {
            accessibilityTitle: `Menu for answer "${answer.text}" for question "${question.text}" in section "${section.name}"`,
            overflowItems: [
                { text: 'Rename', icon: ICON_EDIT_MODIFY_UPDATE, onClickAction: () => setActiveModal({ type: 'renameOption', section, question, answer }) },
                { text: 'Delete', icon: ICON_DELETE_REMOVE, onClickAction: () => setActiveModal({ type: 'deleteOption', section, question, answer }) },
            ],
        };
    };

    const setPoints = (section: InherentRiskQuestionnaireSectionConfiguration, question: IrqQuestionSingleSelect | IrqQuestionMultipleSelect, answer: IrqAnswerOption, points: number) => {
        setSections([...sections.map((s) => (s.name === section.name ? { ...s, questions: s.questions.map((q) => (q.text === question.text && (q._type === IrqQuestionType.SINGLE_SELECT || q._type === IrqQuestionType.MULTIPLE_SELECT) ? { ...q, options: q.options.map((o) => (o.text === answer.text ? { ...o, points } : o)) } : q)) } : s))]);
    };

    const validate = () => {
        if (sections.length === 0) {
            throw new Error('There must be at least one section.');
        }

        if (sections.some((section) => section.questions.length === 0)) {
            throw new Error('Each section must contain at least one question.');
        }

        sections.forEach((section) => {
            section.questions.forEach((question) => {
                if ((question._type === IrqQuestionType.SINGLE_SELECT || question._type === IrqQuestionType.MULTIPLE_SELECT) && question.options.length === 0) {
                    throw new Error(`Each question must contain at least one answer option. Add options to the question "${question.text}" in section "${section.name}".`);
                }
            });
        });
    };

    const saveConfiguration = async () => {
        setRequestState({ type: 'inProgress' });

        try {
            validate();
            await props.moduleApi.setInherentRiskQuestionnaireConfiguration(configuration);
            // No success message is displayed; the disappearance of the unsaved-changes warning in the footer indicates success.
            setRequestState(undefined);
            onConfigurationSaved(configuration);
        } catch (error) {
            setRequestState({ type: 'failure', message: error.message });
        }
    };

    /** Download the Inherent Risk Questionnaire as a CSV file. */
    const downloadCsv = () => {
        const escapeCsvField = (field: string) => {
            if (field.includes(',') || field.includes('"') || field.includes('\n')) {
                return `"${field.replace(/"/g, '""')}"`;
            }
            return field;
        };

        const rows: string[][] = [['Section', 'Question', 'Question Type', 'Possible Answers']];

        sections.forEach((section) => {
            section.questions.forEach((question) => {
                const possibleAnswers: string = (() => {
                    switch (question._type) {
                        case IrqQuestionType.DATE:
                        case IrqQuestionType.FILE_UPLOAD:
                        case IrqQuestionType.FREEFORM_TEXT:
                            return '';
                        case IrqQuestionType.MULTIPLE_SELECT:
                        case IrqQuestionType.SINGLE_SELECT:
                            return question.options.map((option) => option.text).join(', ');
                    }
                })();

                rows.push([section.name, question.text, question._type, possibleAnswers]);
            });
        });

        const csvContent = rows.map((row) => row.map(escapeCsvField).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'InherentRiskQuestionnaire.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    const inherentRiskQuestionnaireConfigurationThresholdsProps: InherentRiskQuestionnaireConfigurationThresholdsProps = {
        defaultValues: [thresholds.low, thresholds.low_moderate, thresholds.moderate, thresholds.moderate_high],
        minimum: minimumPossibleScore,
        maximum: maximumPossibleScore,
        onNewValidValues: handleThresholdsChange,
    };

    return (
        <RBACComponent roles={[Role.ADMIN]}>
            {activeModal?.type === 'addSection' && <AddOrUpdateTextModal {...addSectionProps} />}
            {activeModal?.type === 'addQuestion' && <AddOrUpdateIrqQuestionModal {...getAddQuestionProps(activeModal.section)} />}
            {activeModal?.type === 'addOption' && <AddOrUpdateTextModal {...getAddOptionProps(activeModal.section, activeModal.question)} />}
            {activeModal?.type === 'reorderSections' && <ReorderModal {...reorderSectionsProps} />}
            {activeModal?.type === 'reorderQuestions' && <ReorderModal {...getReorderQuestionsProps(activeModal.section)} />}
            {activeModal?.type === 'reorderOptions' && <ReorderModal {...getReorderOptionsProps(activeModal.section, activeModal.question)} />}
            {activeModal?.type === 'renameSection' && <AddOrUpdateTextModal {...getRenameSectionModalProps(activeModal.section)} />}
            {activeModal?.type === 'editQuestion' && <AddOrUpdateIrqQuestionModal {...getEditQuestionModalProps(activeModal.section, activeModal.questionIndex)} />}
            {activeModal?.type === 'renameOption' && <AddOrUpdateTextModal {...getRenameOptionModalProps(activeModal.section, activeModal.question, activeModal.answer)} />}
            {activeModal?.type === 'deleteSection' && <ConfirmDeleteModal {...getDeleteSectionModalProps(activeModal.section)} />}
            {activeModal?.type === 'deleteQuestion' && <ConfirmDeleteModal {...getDeleteQuestionModalProps(activeModal.section, activeModal.question)} />}
            {activeModal?.type === 'deleteOption' && <ConfirmDeleteModal {...getDeleteOptionModalProps(activeModal.section, activeModal.question, activeModal.answer)} />}

            <div className={styles.contentWithPaddingForFooter}>
                <PageLayoutDefault
                    headerBreadcrumb={
                        <Breadcrumb textColor="blue">
                            <BreadcrumbLink link={generatePath(SETTINGS)}>Settings</BreadcrumbLink>
                            <BreadcrumbText>Inherent Risk Questionnaire Configuration</BreadcrumbText>
                        </Breadcrumb>
                    }
                    headerTitle="Inherent Risk Questionnaire Configuration"
                    headerButtons={
                        <>
                            <Button variant="secondary" onClick={downloadCsv} fontAwesomeImage={ICON_DOWNLOAD}>
                                Download
                            </Button>
                            <Button variant="secondary" onClick={() => setActiveModal({ type: 'reorderSections' })} fontAwesomeImage={ICON_ORDERING}>
                                Reorder Sections
                            </Button>
                            <Button variant="secondary" onClick={() => setActiveModal({ type: 'addSection' })} fontAwesomeImage={ICON_ADD_CREATE}>
                                Add Section
                            </Button>
                        </>
                    }
                    headerDescription={`Configure the questions and scoring used to calculate the inherent risk of ${props.module === ProductModule.UCRM ? 'use cases' : 'third-party services'}.`}
                    body={[
                        {
                            content: (
                                <Box component="form">
                                    <InherentRiskQuestionnaireConfigurationThresholds {...inherentRiskQuestionnaireConfigurationThresholdsProps} />
                                    <div className={styles.accordionHeaderContainer}>
                                        <div className={styles.headerSectionsQuestionsAndAnswers}>
                                            <Text variant="Text3" noStyles>
                                                SECTIONS, QUESTIONS, AND ANSWERS
                                            </Text>
                                        </div>
                                        <div className={styles.headerPoints}>
                                            <Text noStyles>POINTS</Text>
                                        </div>
                                    </div>
                                    {sections.map((section) => (
                                        <Accordion key={section.name}>
                                            <InherentRiskQuestionnaireConfigurationRow level={0} eventKey={section.name} accessibilityTitle={section.name}>
                                                <div className={styles.accordionRow}>
                                                    <div>
                                                        <Text noStyles>{section.name}</Text>
                                                    </div>
                                                    <OverflowMenu {...getOverflowMenuPropsForSection(section)} />
                                                </div>
                                                <hr />
                                            </InherentRiskQuestionnaireConfigurationRow>
                                            <AccordionCollapse eventKey={section.name} accordionFor={section.name}>
                                                <div data-testid={`sectionContent-${section.name}`}>
                                                    {section.questions.map((question, questionIndex) =>
                                                        question._type === IrqQuestionType.SINGLE_SELECT || question._type === IrqQuestionType.MULTIPLE_SELECT ? (
                                                            <Accordion key={question.text}>
                                                                <InherentRiskQuestionnaireConfigurationRow level={1} eventKey={question.text} accessibilityTitle={question.text}>
                                                                    <div className={styles.accordionRow}>
                                                                        <div className={styles.questionTextAndTypeContainer}>
                                                                            <Text noStyles>{question.text}</Text>
                                                                            <Text noStyles variant="Text4" color="darkGray">
                                                                                <i>{question._type}</i>
                                                                            </Text>
                                                                        </div>
                                                                        <OverflowMenu {...getOverflowMenuPropsForQuestion(section, questionIndex)} />
                                                                    </div>
                                                                    <hr />
                                                                </InherentRiskQuestionnaireConfigurationRow>
                                                                <AccordionCollapse eventKey={question.text} accordionFor={question.text}>
                                                                    <div data-testid={`questionContent-${section.name}-${question.text}`}>
                                                                        {question.options.map((option) => (
                                                                            <Accordion key={option.text}>
                                                                                <InherentRiskQuestionnaireConfigurationRow level={2}>
                                                                                    <div className={styles.accordionRow}>
                                                                                        <div>
                                                                                            <label htmlFor={`points-${section.name}-${question.text}-${option.text}`}>
                                                                                                <Text noStyles>{option.text}</Text>
                                                                                            </label>
                                                                                        </div>
                                                                                        <div className={styles.menuAndPointsContainer}>
                                                                                            <div className={styles.pointsContainer}>
                                                                                                <FormFieldText formFieldId={`points-${section.name}-${question.text}-${option.text}`} formFieldType="number" value={option.points} handleChange={(event: React.FormEvent<HTMLInputElement>) => setPoints(section, question, option, Number(event.currentTarget.value))} step={1} />
                                                                                            </div>
                                                                                            <OverflowMenu {...getOverflowMenuPropsForOption(section, question, option)} />
                                                                                        </div>
                                                                                    </div>
                                                                                </InherentRiskQuestionnaireConfigurationRow>
                                                                            </Accordion>
                                                                        ))}
                                                                    </div>
                                                                </AccordionCollapse>
                                                            </Accordion>
                                                        ) : (
                                                            <InherentRiskQuestionnaireConfigurationRow key={question.text} level={1}>
                                                                <div className={styles.accordionRow}>
                                                                    <div className={styles.questionTextAndTypeContainer}>
                                                                        <Text noStyles>{question.text}</Text>
                                                                        <Text noStyles variant="Text4" color="darkGray">
                                                                            <i>{question._type}</i>
                                                                        </Text>
                                                                    </div>
                                                                    <OverflowMenu {...getOverflowMenuPropsForQuestion(section, questionIndex)} />
                                                                </div>
                                                                <hr />
                                                            </InherentRiskQuestionnaireConfigurationRow>
                                                        )
                                                    )}
                                                </div>
                                            </AccordionCollapse>
                                        </Accordion>
                                    ))}
                                </Box>
                            ),
                        },
                    ]}
                />
            </div>

            <FixedFooter
                hasUnsavedChanges={hasUnsavedChanges}
                errorMessage={requestState?.type === 'failure' ? requestState.message : undefined}
                content={
                    <Text variant="Text2" noStyles>
                        Inherent Risk Questionnaire
                    </Text>
                }
                buttons={
                    <Button variant="primary" onClick={saveConfiguration} fontAwesomeImage={ICON_SAVE} loadingText="Saving..." isLoading={requestState?.type === 'inProgress'}>
                        Save
                    </Button>
                }
            />
        </RBACComponent>
    );
};

interface ConfirmDeleteModalProps {
    headerText: string;
    confirmationText: string;
    onConfirm: () => void;
    hideModal: () => void;
}

// TODO [TPRM - IRQ]: If possible, use `ConfirmationModal`. Maybe update it so that it can take a Promise-based callback OR a non-Promise-based callback?
const ConfirmDeleteModal = (props: ConfirmDeleteModalProps) => {
    return (
        <Modal open header={props.headerText}>
            <Box component="form" noValidate>
                <Text>{props.confirmationText}</Text>
                <div className={'modalFormButtonContainer'}>
                    <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                        Close
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            props.onConfirm();
                            props.hideModal();
                        }}
                        fontAwesomeImage={ICON_DELETE_REMOVE}
                    >
                        Delete
                    </Button>
                </div>
            </Box>
        </Modal>
    );
};
